import { useEffect, useRef, useState } from "react";
import { getMessageTemplates, sendLeadMessage } from "../services/api";
import { track } from "../services/analytics";

const fillTemplate = (content, lead) => {
  const variables = {
    first_name: lead.first_name || "",
    last_name: lead.last_name || "",
    full_name: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
    phone: lead.phone || "",
    email: lead.email || "",
    booking_url: lead.location?.effective_booking_url || "",
  };
  return Object.keys(variables).reduce(
    (text, key) => text.replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), variables[key]),
    content,
  );
};

// Chips skip appointment-only templates and lead with a booking one.
// The select keeps the full list.
const chipTemplates = (templates) => {
  const relevant = templates.filter((t) => !/confirm|remind/i.test(t.name));
  const isBooking = (t) => /booking/i.test(t.name);
  return [...relevant.filter(isBooking), ...relevant.filter((t) => !isBooking(t))].slice(0, 3);
};

/**
 * The message composer: textarea, template select and Send. Shows why texting
 * is off when the lead cannot be texted. Shared by the act row (full page) and
 * the Conversation card (sidecart).
 *
 * SMS only. Email is disabled product-wide (see emailEnabled in LeadMessaging).
 */
const LeadComposer = ({ authToken, lead, onSent, autoFocus = false, textareaRef: outerRef, insertRef, surface = "act_row", showTemplateChips = false }) => {
  const [content, setContent] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const ownRef = useRef(null);
  const textareaRef = outerRef || ownRef;

  const hasPhone = !!lead.phone;
  const canText = hasPhone && (lead.location?.messaging_enabled ?? false);
  useEffect(() => {
    if (!canText) return;
    getMessageTemplates()
      .then((response) => setTemplates(response?.results || response || []))
      // Templates are optional, so a failed fetch only hides the select.
      .catch((err) => console.error("Failed to fetch templates:", err));
  }, [canText]);

  // A different lead is a different conversation: drop the draft. Done in
  // render, not in an effect, so a late effect can never wipe new typing.
  const [draftLeadId, setDraftLeadId] = useState(lead.id);
  if (draftLeadId !== lead.id) {
    setDraftLeadId(lead.id);
    setContent("");
    setSelectedTemplate("");
    setError(null);
  }

  const handleTemplateSelect = (templateId, known) => {
    setSelectedTemplate(templateId);
    const template = known || templates.find((t) => t.id.toString() === templateId);
    if (!template) return;
    const text = fillTemplate(template.content, lead);
    // Insert at the cursor, so a template never wipes what was typed.
    setContent((prev) => {
      if (!prev) return text;
      const cursor = textareaRef.current?.selectionStart ?? prev.length;
      return `${prev.slice(0, cursor)}${text}${prev.slice(cursor)}`;
    });
    textareaRef.current?.focus();
  };

  // Lets the Conversation card put a template in the box (Next action chip).
  if (insertRef) {
    insertRef.current = (template) => handleTemplateSelect(template.id.toString(), template);
  }

  const handleSend = async () => {
    if (!content.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await sendLeadMessage(authToken, lead.id, "sms", content);
      track("lead_message_sent", {
        channel: "sms",
        used_template: !!selectedTemplate,
        template_id: selectedTemplate || null,
        body_length: content.length,
        surface,
      });
      setContent("");
      setSelectedTemplate("");
      if (onSent) onSent();
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Unauthorized: Please log in again."
          : "Failed to send message",
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Focus on open only when a patient reply is waiting.
  const focusLeadId = useRef(null);
  useEffect(() => {
    if (autoFocus && canText && focusLeadId.current !== lead.id) {
      focusLeadId.current = lead.id;
      textareaRef.current?.focus();
    }
  }, [autoFocus, canText, lead.id, textareaRef]);

  return (
    <>
      {canText ? (
        <div className="rounded-lg border border-border focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition-all">
          <textarea
            ref={textareaRef}
            data-attr="lead-message-input"
            aria-label="Message"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            rows={2}
            placeholder={`Text ${lead.first_name || "patient"}...`}
            className="block w-full resize-none rounded-t-lg border-0 bg-transparent px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-0 disabled:opacity-60"
          />
          {showTemplateChips && chipTemplates(templates).length > 0 && (
            <div
              data-testid="template-chips"
              className="flex flex-wrap gap-1.5 border-t border-border px-2 py-1.5"
            >
              {chipTemplates(templates).map((template) => (
                <button
                  key={template.id}
                  type="button"
                  data-attr="lead-use-template"
                  onClick={() => handleTemplateSelect(template.id.toString())}
                  className="max-w-[10rem] truncate rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {template.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 border-t border-border px-2 py-1.5">
            {templates.length > 0 && (
              <select
                data-attr="lead-use-template"
                aria-label="Template"
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="min-w-0 max-w-[14rem] flex-1 truncate rounded border-0 bg-gray-50 py-1 pl-2 pr-7 text-xs text-gray-700 focus:ring-2 focus:ring-primary"
              >
                <option value="">Use a template...</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              data-attr="lead-send-message"
              onClick={handleSend}
              disabled={sending || !content.trim()}
              className="ml-auto rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-primary/50 transition-all"
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          {hasPhone
            ? "Texting is not on for this location. Ask your marketing agency to turn it on."
            : "Add a phone number to text or call this lead."}
        </p>
      )}

      {error && <p className="mt-1.5 text-xs font-medium text-red-700">{error}</p>}
    </>
  );
};

export default LeadComposer;
